import * as THREE from "three";
import { resolveVariantIndex } from "../utils/variants.js";

// A placed element the host's position picker can address.
const hasVariantChoice = (element) => (element?.furnitureData?.properties?.variants?.length ?? 0) > 1;

/** Single-element loading (`?element=`) and the host-driven position API of an embed. */
export class EmbedController {
  constructor(app, uiManager) {
    this.app = app;
    this.ui = uiManager;
  }

  /**
   * Spawn a single catalog element by urlCode (from `?element=`), clearing the scene
   * first. Optional fabric/leg overrides are applied on top, same as presets.
   * @returns {Promise<boolean>} true if the element was found and spawned
   */
  async loadElementByUrlCode(urlCode, overrides = null) {
    const furnitureManager = this.app.getFurnitureManager();
    if (!furnitureManager) return false;

    const itemData = furnitureManager.findCatalogItemByUrlCode(urlCode);
    if (!itemData) {
      console.warn(`No catalog element found for urlCode "${urlCode}"`);
      return false;
    }

    furnitureManager.clearAll();

    furnitureManager.seedLegHeightFor(itemData);

    const element = await furnitureManager.spawnFurniture(itemData);
    if (!element) return false;

    // ?position= picks the variant, applied instantly — an offscreen capture never advances a tween.
    // No collision system: a lone element has nothing to clear.
    const variantIndex = overrides?.position
      ? this.variantIndexFor(itemData, overrides.position)
      : null;
    if (variantIndex != null) element.switchVariant(variantIndex, null, { animate: false });

    const yaw = this.embedYawFor(itemData, overrides?.elementYaw);
    if (yaw) element.getMesh().rotation.y = THREE.MathUtils.degToRad(yaw);

    // Variant footprints differ, so re-centre — after the yaw, since rotation is about the origin.
    // Unconditional on a resolved ?position=: switchVariant() also returns false for a no-op switch.
    if (variantIndex != null) {
      element.computeBoundingBoxFull();
      element.setCenterXZ(0, 0);
    }

    // spawnFurniture alone leaves the element white; this applies the default fabric.
    this.ui.finalizeFurniturePlacement(furnitureManager, element);

    // Sync the catalog grid — skipped in embed, where the grid UI isn't built.
    if (itemData.collection && !this.app.isEmbedMode) {
      await this.ui.setFurnitureCollectionFilter(itemData.collection, false);
      this.ui.updateLegHeightDropdown();
    }

    if (overrides) await this.ui.applyPresetOverrides(overrides);
    return true;
  }

  /**
   * Host-driven variant switch for a live embed (`postMessage { type: 'setOptions', position }`).
   * Unlike the `?position=` capture path it animates by default — the fold is what the shopper came
   * to see — and resolves the index directly rather than through variantIndexFor(), whose
   * notifications would land as a toast over the product shot; the host is told the outcome instead.
   *
   * Targets are the elements carrying `opts.target`'s urlCode, else every multi-variant element;
   * each resolves against its own variant list, so a name a module doesn't know leaves it alone.
   *
   * @param {string|number} position variant name (case-insensitive) or index
   * @param {{target?: string|null, animate?: boolean}} [opts]
   * @returns {string|null} the catalog variant name that resolved, null if none did
   */
  applyEmbedPosition(position, opts = {}) {
    const furnitureManager = this.app.getFurnitureManager();
    if (!furnitureManager || position == null || position === '') return null;

    const all = furnitureManager.getAllFurniture();
    const targets = opts.target
      ? all.filter((f) => f.furnitureData?.urlCode === opts.target)
      : all.filter(hasVariantChoice);

    const animate = opts.animate !== false;
    let resolved = null;
    const switched = [];

    for (const element of targets) {
      const variants = element.furnitureData?.properties?.variants;
      const index = resolveVariantIndex(variants, position);
      if (index === null) continue;

      resolved = variants[index]?.name ?? String(position);
      // No collision system: an embed is read-only, and the host asked for this pose.
      if (element.switchVariant(index, null, { animate })) {
        switched.push(element);
        furnitureManager.finalizeVariantSwitch(element);
      }
    }

    // A lone element embed is framed on the origin and variant footprints differ, so re-centre it
    // where the capture path does. Instant only: mid-fold would slide the piece out of the shot.
    if (!animate && this.app.embedElement && all.length === 1 && switched.length) {
      switched[0].computeBoundingBoxFull();
      switched[0].setCenterXZ(0, 0);
    }
    return resolved;
  }

  /**
   * What a host picker needs: the variant names this embed answers to (catalog order, deduped) and
   * the pose currently held. Sent with `embedReady` and echoed after every applied message — read
   * back from the scene, so a position the product has no variant for reports the unchanged one.
   * @returns {{position: string|null, positions: string[]}} empty when nothing in scene has variants
   */
  embedPositionState() {
    const all = this.app.getFurnitureManager()?.getAllFurniture() ?? [];
    const names = new Set();
    let position = null;
    for (const element of all) {
      if (!hasVariantChoice(element)) continue;
      const variants = element.furnitureData.properties.variants;
      position ??= variants[element.variantIndex]?.name ?? null;
      for (const variant of variants) if (variant?.name) names.add(variant.name);
    }
    return { position, positions: [...names] };
  }

  // `?position=` to a variant index, with the notifications the shared resolver has no business firing.
  variantIndexFor(itemData, position) {
    const variants = itemData?.properties?.variants;
    if (!variants?.length) {
      this.ui.showNotification(`"${itemData?.urlCode}" has no positions to choose from — ignoring.`, 'warning');
      return null;
    }
    const index = resolveVariantIndex(variants, position);
    if (index === null) {
      const names = variants.map((v) => v?.name).filter(Boolean).join(', ');
      this.ui.showNotification(`Position "${position}" isn't one of: ${names} — keeping the default.`, 'warning');
    }
    return index;
  }

  /**
   * Lone-element yaw in degrees: `?elementYaw=`, then its `embedYaw`, then 0 (models face the camera on +Z).
   * @param {Object} itemData catalog entry
   * @param {string|number|null} [override] raw `?elementYaw=` value
   * @returns {number} degrees
   */
  embedYawFor(itemData, override = null) {
    if (override != null && override !== '') {
      const deg = Number(override);
      if (Number.isFinite(deg)) return deg;
      this.ui.showNotification(`Element yaw "${override}" isn't a number — ignoring.`, 'warning');
    }
    const declared = itemData?.properties?.embedYaw;
    return Number.isFinite(declared) ? declared : 0;
  }
}
